<script setup lang="ts">
import { ref, shallowRef, watch, onBeforeUnmount, computed, onErrorCaptured, onMounted, h, render } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import { OrderedList } from '@tiptap/extension-ordered-list'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
// Underline is included in StarterKit v3 by default, no separate import needed
import TextAlign from '@tiptap/extension-text-align'
import Highlight from '@tiptap/extension-highlight'
import Collaboration from '@tiptap/extension-collaboration'
import Mention from '@tiptap/extension-mention'
// CollaborationCursor temporarily disabled - requires specific provider interface
// import CollaborationCursor from '@tiptap/extension-collaboration-cursor'
import * as Y from 'yjs'
import { IndexeddbPersistence } from 'y-indexeddb'
import { SocketIOProvider, type InvalidateInfo, type JoinedInfo } from '@/utils/SocketIOProvider'
import { attachmentsApi, aiApi, peopleApi } from '@/plugins/api'
import MentionList from './MentionList.vue'
import { useAuthStore } from '@/stores/auth'
import { htmlToMd, markdownToEditorHtml } from '@/utils/markdown'
import { serialLatest } from '@/utils/serialLatest'
// Note: useAuthStore will be needed for CollaborationCursor (user name/color)

// Содержимое поля → HTML для редактора (не-HTML всегда считается markdown)
const ensureHtml = markdownToEditorHtml

const props = defineProps<{
  modelValue?: string
  placeholder?: string
  taskId?: string       // Legacy: for tasks
  entityType?: string   // Generic: 'task' or 'article'
  entityId?: string     // Generic: entity UUID
  field?: string
  minHeight?: string
  readonly?: boolean
  collaborative?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'imageUploaded': [url: string]
}>()

const uploading = ref(false)
const aiProcessing = ref(false)
const aiMenuOpen = ref(false)
// /ai/improve-text закрыт для viewer — кнопку AI показываем editor+ и executive
const authStore = useAuthStore()
const canUseAi = computed(() =>
  ['editor', 'executive', 'admin', 'superadmin'].some(r => authStore.roles.includes(r)),
)

// Flag to prevent watch from triggering setContent when change originates from editor
// This fixes cursor jumping issue when editing in the middle of text
const isInternalUpdate = ref(false)

// AI improvement modes
type AiImproveMode = 'fix_errors' | 'structure' | 'clarify' | 'all'

async function improveWithAi(mode: AiImproveMode) {
  console.log('[MarkdownEditor] AI improve requested, mode:', mode)

  if (!editor.value) {
    console.warn('[MarkdownEditor] AI improve skipped: editor not initialized')
    return
  }

  const currentContent = editor.value.getHTML()
  // Check for empty content (TipTap returns <p></p> for empty editor)
  const isEmptyContent = !currentContent || currentContent === '<p></p>' || currentContent.trim() === ''
  console.log('[MarkdownEditor] Current content:', { length: currentContent?.length ?? 0, isEmpty: isEmptyContent, content: currentContent?.substring(0, 100) })

  if (isEmptyContent) {
    console.warn('[MarkdownEditor] AI improve skipped: editor content is empty')
    return
  }

  if (aiProcessing.value) {
    console.warn('[MarkdownEditor] AI improve skipped: already processing')
    return
  }

  aiProcessing.value = true
  aiMenuOpen.value = false

  try {
    // Get task context if available (only for tasks, not articles)
    let taskContext: string | undefined
    const { entityType, entityId } = getEntityInfo()
    console.log('[MarkdownEditor] Entity info:', { entityType, entityId })

    if (entityType === 'task' && entityId) {
      try {
        const contextResponse = await aiApi.getTaskContext(entityId)
        taskContext = contextResponse.data.markdown
        console.log('[MarkdownEditor] Got task context, length:', taskContext?.length)
      } catch (err) {
        console.warn('[MarkdownEditor] No task context available:', err)
      }
    }

    console.log('[MarkdownEditor] Calling improveText API...')
    const response = await aiApi.improveText({
      text: currentContent,
      mode,
      taskContext,
    })
    console.log('[MarkdownEditor] AI improve response:', response.data)

    if (response.data.improved && response.data.improved !== currentContent) {
      editor.value?.commands.setContent(response.data.improved, { emitUpdate: true })
      console.log('[MarkdownEditor] Content updated with AI improvements')
    } else {
      console.log('[MarkdownEditor] No changes from AI')
    }
  } catch (error) {
    console.error('[MarkdownEditor] AI improve failed:', error)
  } finally {
    aiProcessing.value = false
  }
}

// Collaborative editing state
const ydoc = shallowRef<Y.Doc | null>(null)
const wsProvider = shallowRef<SocketIOProvider | null>(null)
const indexeddbProvider = shallowRef<IndexeddbPersistence | null>(null)
const isConnected = ref(false)
const isSynced = ref(false)
const isOffline = ref(!navigator.onLine)
const collaborators = ref<{ id: string; name: string; color: string }[]>([])

// Generate random color for user cursor (for future CollaborationCursor)
// function getRandomColor(): string {
//   const colors = [
//     '#958DF1', '#F98181', '#FBBC88', '#FAF594',
//     '#70CFF8', '#94FADB', '#B9F18D', '#C3E2C2',
//     '#EAECCC', '#AFC8AD', '#EEC759', '#9BB8CD',
//   ]
//   return colors[Math.floor(Math.random() * colors.length)]
// }

// Build extensions based on mode
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildExtensions(isCollaborative: boolean): any[] {
  const extensions: unknown[] = [
     
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      link: false, // We configure Link separately
      // IMPORTANT: Disable undoRedo for collaborative mode - Collaboration extension has its own history
      // TipTap v3 uses 'undoRedo' instead of 'history'
      undoRedo: isCollaborative ? false : undefined,
      // Disable underline in StarterKit as we'll use the one from StarterKit's default
      // (This prevents duplicate extension warning if any other extension adds underline)
      // FIX :t1004166 - Disable orderedList in StarterKit, add custom version without inputRules
      orderedList: false,
    }),
    // Custom OrderedList without inputRules to fix "1." being swallowed on input
    // Users can still create numbered lists via toolbar button (Mod-Shift-7)
    OrderedList.extend({
      addInputRules() {
        return [] // Disable inputRules that cause "1." to disappear
      },
    }),
    Image.configure({
      inline: true,
      allowBase64: true,
    }),
    Link.configure({
      openOnClick: true,
      HTMLAttributes: {
        class: 'text-primary',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    }),
    Placeholder.configure({
      placeholder: props.placeholder || 'Начните писать...',
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Highlight.configure({}),
    // Note: Underline is included in StarterKit v3 by default
    // Mention extension for @mentions
    Mention.configure({
      HTMLAttributes: {
        class: 'mention',
      },
      suggestion: {
        items: async ({ query }: { query: string }) => {
          if (!query || query.length < 2) return []
          try {
            const response = await peopleApi.getAll({ search: query, limit: 10 })
            return (response.data || []).map((p: { id: string; fullName: string; email?: string }) => ({
              id: p.id,
              fullName: p.fullName,
              email: p.email,
            }))
          } catch (e) {
            console.warn('[MarkdownEditor] Failed to fetch people for mention:', e)
            return []
          }
        },
        render: () => {
          let component: ReturnType<typeof h> | null = null
          let popup: HTMLElement | null = null
          let componentInstance: { onKeyDown: (event: KeyboardEvent) => boolean } | null = null

          return {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onStart: (props: any) => {
              popup = document.createElement('div')
              popup.className = 'mention-popup'
              document.body.appendChild(popup)

              component = h(MentionList, {
                items: props.items,
                command: props.command,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ref: (el: any) => {
                  componentInstance = el
                },
              })
              render(component, popup)

              // Position popup near the cursor
              const rect = props.clientRect?.()
              if (rect && popup) {
                popup.style.position = 'absolute'
                popup.style.left = `${rect.left}px`
                popup.style.top = `${rect.bottom + 5}px`
                popup.style.zIndex = '9999'
              }
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onUpdate: (props: any) => {
              if (popup) {
                component = h(MentionList, {
                  items: props.items,
                  command: props.command,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ref: (el: any) => {
                    componentInstance = el
                  },
                })
                render(component, popup)

                const rect = props.clientRect?.()
                if (rect) {
                  popup.style.left = `${rect.left}px`
                  popup.style.top = `${rect.bottom + 5}px`
                }
              }
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onKeyDown: (props: any) => {
              if (props.event.key === 'Escape') {
                if (popup) {
                  render(null, popup)
                  popup.remove()
                  popup = null
                }
                return true
              }
              return componentInstance?.onKeyDown(props.event) ?? false
            },
            onExit: () => {
              if (popup) {
                render(null, popup)
                popup.remove()
                popup = null
              }
              componentInstance = null
            },
          }
        },
      },
    }),
  ]

  // Add collaborative extensions when enabled
  if (isCollaborative && ydoc.value) {
    console.log('[MarkdownEditor] Adding Collaboration extension with fragment')
    extensions.push(
      Collaboration.configure({
        // Use fragment directly - more reliable than document+field
        fragment: ydoc.value.getXmlFragment('default'),
      }),
    )

    // TODO: Re-enable CollaborationCursor after fixing provider interface
    // The cursor extension requires specific provider interface that our
    // custom SocketIOProvider may not fully implement
    // if (wsProvider.value) {
    //   extensions.push(
    //     CollaborationCursor.configure({
    //       provider: wsProvider.value,
    //       user: {
    //         name: authStore.user?.name || 'Anonymous',
    //         color: getRandomColor(),
    //       },
    //     }),
    //   )
    // }
  }

  return extensions
}

// Editor instance as a shallowRef
const editor = shallowRef<Editor | null>(null)
const editorError = ref<string | null>(null)
// Показан текст только для чтения, пока другой клиент наполняет документ
const previewMode = ref(false)
// Текст редактора до сброса, не совпавший с новым: правки, сделанные без
// связи с сервером, после сброса не применяются — показать и дать скопировать
const discardedText = ref<string | null>(null)

// Create editor with proper configuration.
// preview — редактор только для чтения с текстом из базы: пустой общий
// документ наполняет другой клиент, до этого показываем текст как есть.
function createEditor(isCollaborative: boolean, preview?: { content: string }) {
  console.log('[MarkdownEditor] Creating editor, collaborative:', isCollaborative)

  try {
    const extensions = buildExtensions(isCollaborative)
    console.log('[MarkdownEditor] Extensions built:', extensions.length, 'extensions')

    editor.value = new Editor({
      // For collaborative mode, DON'T set content - Yjs provides it
      // For non-collaborative, use modelValue (convert markdown to HTML if needed)
      content: isCollaborative ? undefined : ensureHtml(preview ? preview.content : props.modelValue || ''),
      editable: preview ? false : !props.readonly,
      extensions,
      onCreate: ({ editor: e }) => {
        console.log('[MarkdownEditor] onCreate fired, editor created:', e ? 'yes' : 'no')
      },
      onUpdate: ({ editor: e }) => {
        // Set flag to prevent watch from re-setting content (fixes cursor jump)
        isInternalUpdate.value = true
        // Emit as Markdown (DB stores MD now)
        // For non-collaborative this triggers v-model
        // For collaborative, this keeps parent component in sync (used by auto-save status)
        emit('update:modelValue', htmlToMd(e.getHTML()))
        // Reset flag after Vue's reactivity cycle completes
        setTimeout(() => {
          isInternalUpdate.value = false
        }, 0)
      },
      onBlur: () => {
        // Finalize document when user leaves editor (triggers history/annotation)
        if (isCollaborative && wsProvider.value?.connected) {
          console.log('[MarkdownEditor] Editor blur - finalizing document')
          wsProvider.value.finalize()
        }
      },
      editorProps: {
        attributes: {
          spellcheck: 'true',
          lang: 'ru',
        },
        handlePaste: (_view, event) => {
          const items = event.clipboardData?.items
          if (!items) return false

          for (const item of Array.from(items)) {
            if (item.type.startsWith('image/')) {
              const file = item.getAsFile()
              if (file) {
                event.preventDefault()
                handleImageUpload(file)
                return true
              }
            }
          }
          return false
        },
        handleDrop: (_view, event) => {
          const files = event.dataTransfer?.files
          if (!files || files.length === 0) return false

          for (const file of Array.from(files)) {
            if (file.type.startsWith('image/')) {
              event.preventDefault()
              handleImageUpload(file)
              return true
            }
          }
          return false
        },
      },
    })
    console.log('[MarkdownEditor] Editor created successfully')
  } catch (err) {
    console.error('[MarkdownEditor] Failed to create editor:', err)
    const errorMsg = err instanceof Error ? `${err.message}\n${err.stack}` : String(err)
    editorError.value = errorMsg
  }
}

// Get effective entity type and ID (supports both legacy taskId and new entityType/entityId)
function getEntityInfo() {
  const entityType = props.entityType || 'task'
  const entityId = props.entityId || props.taskId
  return { entityType, entityId }
}

// Clear IndexedDB and return a promise
function clearIndexedDB(dbName: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const dbRequest = indexedDB.deleteDatabase(dbName)
      dbRequest.onsuccess = () => {
        console.log('[MarkdownEditor] Cleared stale IndexedDB for', dbName)
        resolve()
      }
      dbRequest.onerror = () => {
        console.warn('[MarkdownEditor] Error clearing IndexedDB for', dbName)
        resolve() // Continue anyway
      }
      dbRequest.onblocked = () => {
        console.warn('[MarkdownEditor] IndexedDB delete blocked for', dbName)
        resolve() // Continue anyway
      }
      // Timeout fallback in case callbacks never fire
      setTimeout(() => resolve(), 500)
    } catch (e) {
      console.warn('[MarkdownEditor] Could not clear IndexedDB:', e)
      resolve()
    }
  })
}

// IndexedDB: локальная копия документа (офлайн-правки). Имя включает эпоху
// документа: копия прежней эпохи (документ сброшен правкой поля мимо
// редактора) не подмешивается к новому документу и не возвращает старый текст.
// v3 — после исправления «сплющенных» описаний (2.24.3).
const IDB_VERSION_PREFIX = 'v3:'
const EPOCH_STORAGE_PREFIX = 'crdt-epoch:'
// Наполнение эпохи отправлено, но сервер его ещё не подтвердил. Если оно
// проиграло (документ наполнил другой клиент), локальная копия этой эпохи
// содержит своё наполнение и при следующем входе задвоила бы текст.
const PENDING_INIT_PREFIX = 'crdt-init-pending:'

function readPendingInit(roomName: string): number | null {
  try {
    const raw = localStorage.getItem(PENDING_INIT_PREFIX + roomName)
    const n = raw === null ? NaN : Number(raw)
    return Number.isInteger(n) ? n : null
  } catch {
    return null
  }
}

function writePendingInit(roomName: string, epoch: number | null): void {
  try {
    if (epoch === null) localStorage.removeItem(PENDING_INIT_PREFIX + roomName)
    else localStorage.setItem(PENDING_INIT_PREFIX + roomName, String(epoch))
  } catch {
    // Хранилище недоступно — остаётся окно, описанное выше
  }
}

function idbNameFor(roomName: string, epoch: number): string {
  return `${IDB_VERSION_PREFIX}${roomName}@${epoch}`
}

function readStoredEpoch(roomName: string): number | null {
  try {
    const raw = localStorage.getItem(EPOCH_STORAGE_PREFIX + roomName)
    const n = raw === null ? NaN : Number(raw)
    return Number.isInteger(n) ? n : null
  } catch {
    return null
  }
}

function writeStoredEpoch(roomName: string, epoch: number): void {
  try {
    localStorage.setItem(EPOCH_STORAGE_PREFIX + roomName, String(epoch))
  } catch {
    // Хранилище недоступно — копия просто не переживёт перезагрузку
  }
}

function roomNameOf(): string | null {
  const { entityType, entityId } = getEntityInfo()
  if (!entityId || !props.field) return null
  return `${entityType}:${entityId}:${props.field}`
}

// Поколение сессии совместного редактирования: асинхронные шаги прежней
// сессии (после сброса или размонтирования) ничего не меняют
let collabSession = 0
let disposed = false
// Эпоха документа, с которым работает редактор
let currentEpoch: number | null = null
// Отписка от ожидания содержимого (режим предпросмотра)
let stopWaitingForContent: (() => void) | null = null
// Текст редактора перед сбросом (для сравнения с новым)
let textBeforeReset: string | null = null

async function copyDiscarded() {
  if (discardedText.value === null) return
  try {
    await navigator.clipboard.writeText(discardedText.value)
  } catch (e) {
    console.warn('[MarkdownEditor] Clipboard unavailable:', e)
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/** Остановить сессию: провайдеры, редактор, документ */
function teardownCollaboration() {
  collabSession++
  stopWaitingForContent?.()
  stopWaitingForContent = null
  wsProvider.value?.destroy()
  indexeddbProvider.value?.destroy()
  editor.value?.destroy()
  ydoc.value?.destroy()
  wsProvider.value = null
  indexeddbProvider.value = null
  editor.value = null
  ydoc.value = null
  isConnected.value = false
  isSynced.value = false
  previewMode.value = false
}

// Initialize collaborative editing
function initCollaboration() {
  const roomName = roomNameOf()
  if (!roomName) {
    console.error('[MarkdownEditor] Collaborative mode requires entityId and field props')
    // Fall back to non-collaborative
    createEditor(false)
    return
  }

  const session = ++collabSession
  const storedEpoch = readStoredEpoch(roomName)
  console.log('[MarkdownEditor] Initializing collaboration for room:', roomName, 'known epoch:', currentEpoch ?? storedEpoch)

  const doc = new Y.Doc()
  ydoc.value = doc
  // Initialize the XmlFragment before connecting providers
  doc.getXmlFragment('default')

  // Локальная копия подключается только после входа: эпоху знает лишь сервер
  const provider = new SocketIOProvider(window.location.origin, roomName, doc, {
    autoConnect: true,
    epoch: currentEpoch ?? storedEpoch,
  })
  wsProvider.value = provider

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  provider.on('status', (event: any) => {
    const status = event?.status || event
    console.log('[MarkdownEditor] WebSocket status:', status)
    isConnected.value = status === 'connected'
  })

  provider.on('sync', (synced: unknown) => {
    isSynced.value = !!synced
  })

  provider.on('joined', (info: unknown, firstJoin: unknown) => {
    if (firstJoin) {
      void onJoined(session, roomName, storedEpoch, info as JoinedInfo)
    } else {
      onRejoined(session, info as JoinedInfo)
    }
  })

  provider.on('invalidate', (info: unknown) => {
    void handleInvalidate(info as InvalidateInfo)
  })

  // Роль инициализатора перешла к этому клиенту
  provider.on('init-lease', (data: unknown) => {
    const content = (data as { content?: string })?.content ?? ''
    if (session === collabSession) initializeFromContent(session, content)
  })

  provider.on('join-error', (error: unknown) => {
    if (session !== collabSession) return
    if (error === 'upgrade_required') {
      editorError.value = 'Вышла новая версия редактора — обновите страницу.'
    }
  })

  // Track collaborators via awareness
  provider.awareness.on('change', () => {
    const states = provider.awareness.getStates()
    const users: { id: string; name: string; color: string }[] = []

    states.forEach((state, clientId) => {
      if (clientId !== doc.clientID && state.user) {
        users.push({
          id: String(clientId),
          name: state.user.name || 'Anonymous',
          color: state.user.color || '#888888',
        })
      }
    })

    collaborators.value = users
  })
}

/**
 * Первый вход в комнату: эпоха известна — подключить локальную копию этой
 * эпохи и создать редактор. Пустой документ наполняет только клиент, которому
 * сервер поручил это (init), текстом из базы, а не значением формы.
 */
async function onJoined(session: number, roomName: string, storedEpoch: number | null, info: JoinedInfo) {
  if (session !== collabSession || disposed) return
  const doc = ydoc.value
  if (!doc) return
  currentEpoch = info.epoch

  // Копии прежних эпох и копия без эпохи (до сброса по эпохам) больше не нужны
  if (storedEpoch !== null && storedEpoch !== info.epoch) {
    void clearIndexedDB(idbNameFor(roomName, storedEpoch))
  }
  void clearIndexedDB(`${IDB_VERSION_PREFIX}${roomName}`)
  writeStoredEpoch(roomName, info.epoch)

  // Неподтверждённое наполнение прошлой сессии — его копию не подмешивать
  const pendingInit = readPendingInit(roomName)
  if (pendingInit !== null) {
    await clearIndexedDB(idbNameFor(roomName, pendingInit))
    writePendingInit(roomName, null)
    if (session !== collabSession || disposed) return
  }

  const idbName = idbNameFor(roomName, info.epoch)
  const needsContent = info.init || info.waiting
  // Общий документ этой эпохи пуст — локальная копия той же эпохи могла
  // остаться от неудавшегося наполнения: не подмешивать (задвоила бы текст)
  if (needsContent) await clearIndexedDB(idbName)
  if (session !== collabSession || disposed) return

  const idb = new IndexeddbPersistence(idbName, doc)
  indexeddbProvider.value = idb
  await Promise.race([idb.whenSynced, delay(2000)])
  if (session !== collabSession || disposed) return

  const content = info.content ?? ''
  if (needsContent) {
    // Родитель получает актуальный текст из базы (его копия могла устареть)
    emit('update:modelValue', content)
  }
  const fragment = doc.getXmlFragment('default')
  if (info.init && fragment.length === 0 && content) {
    initializeFromContent(session, content)
  } else if (info.waiting && fragment.length === 0 && content) {
    showPreviewUntilFilled(session, content)
  } else {
    createEditor(true)
  }

  const before = textBeforeReset
  textBeforeReset = null
  if (before && before.trim()) {
    const now = needsContent
      ? content
      : editor.value ? htmlToMd(editor.value.getHTML()) : ''
    if (now.trim() !== before.trim()) discardedText.value = before
  }
}

/**
 * Переподключение в той же эпохе: если сервер назначил этот клиент
 * инициализатором (документ пуст, например под потерял память), наполнить.
 */
function onRejoined(session: number, info: JoinedInfo) {
  const doc = ydoc.value
  if (session !== collabSession || disposed || !doc) return
  const content = info.content ?? ''
  if (info.init && doc.getXmlFragment('default').length === 0 && content) {
    initializeFromContent(session, content)
  }
}

/** Наполнить пустой общий документ текстом из базы (только инициализатор) */
function initializeFromContent(session: number, content: string) {
  const doc = ydoc.value
  if (session !== collabSession || disposed || !doc) return
  stopWaitingForContent?.()
  stopWaitingForContent = null
  if (previewMode.value) {
    editor.value?.destroy()
    editor.value = null
    previewMode.value = false
  }
  if (!editor.value) createEditor(true)
  const ed = editor.value as Editor | null
  if (!ed) return
  // Документ успели наполнить — ничего не делать
  if (doc.getXmlFragment('default').length > 0 || !content) return
  console.log('[MarkdownEditor] Initializing shared document from server content, length:', content.length)
  // Сначала содержимое, затем флаг: первое же обновление несёт текст
  const roomName = roomNameOf()
  const provider = wsProvider.value
  if (roomName && provider && currentEpoch !== null) {
    writePendingInit(roomName, currentEpoch)
    // Первое подтверждение после наполнения — подтверждение наполнения
    const onAck = (response: unknown) => {
      provider.off('update-ack', onAck)
      if ((response as { success?: boolean } | undefined)?.success) {
        writePendingInit(roomName, null)
      }
    }
    provider.on('update-ack', onAck)
  }
  ed.commands.setContent(ensureHtml(content))
  doc.getMap('config').set('initialContentLoaded', true)
}

/** Текст только для чтения, пока общий документ не наполнит инициализатор */
function showPreviewUntilFilled(session: number, content: string) {
  const doc = ydoc.value
  if (!doc) return
  previewMode.value = true
  createEditor(false, { content })
  const fragment = doc.getXmlFragment('default')
  const onUpdate = () => {
    if (fragment.length === 0 || session !== collabSession) return
    stopWaitingForContent?.()
    stopWaitingForContent = null
    editor.value?.destroy()
    editor.value = null
    previewMode.value = false
    createEditor(true)
  }
  doc.on('update', onUpdate)
  stopWaitingForContent = () => doc.off('update', onUpdate)
}

/**
 * Сброс документа (поле изменили мимо редактора, клиент отстал). Повторный
 * сброс во время переинициализации не запускает её параллельно, а
 * выполняется после текущей; после размонтирования — ничего.
 */
const handleInvalidate = serialLatest<InvalidateInfo>(
  info => resetCollaboration(info),
  () => disposed,
)

async function resetCollaboration(info: InvalidateInfo) {
  console.warn('[MarkdownEditor] Document invalidated:', info.reason, 'epoch:', info.epoch)
  const roomName = roomNameOf()
  const oldEpoch = currentEpoch
  // Клиент был без связи (переподключение в новую эпоху) или писал в уже
  // сброшенный документ: его текст мог не дойти до сервера
  if (
    (info.reason === 'epoch_changed' || info.reason === 'stale_epoch') &&
    editor.value && !previewMode.value
  ) {
    textBeforeReset = htmlToMd(editor.value.getHTML())
  }
  // Сначала остановить запись в локальную копию, затем стереть её
  teardownCollaboration()
  if (roomName && oldEpoch !== null) {
    await clearIndexedDB(idbNameFor(roomName, oldEpoch))
  }
  if (typeof info.epoch === 'number') currentEpoch = info.epoch
  if (disposed) return
  console.log('[MarkdownEditor] Reinitializing after invalidation...')
  initCollaboration()
}

// Create editor in onMounted to avoid SSR issues
onMounted(() => {
  console.log('[MarkdownEditor] Component mounted, collaborative:', props.collaborative)

  // Listen for online/offline events
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      console.log('[MarkdownEditor] Browser online')
      isOffline.value = false
    })
    window.addEventListener('offline', () => {
      console.log('[MarkdownEditor] Browser offline')
      isOffline.value = true
    })
  }

  if (props.collaborative) {
    initCollaboration()
  } else {
    createEditor(false)
  }
})

// Capture errors during mount to see what's happening
onErrorCaptured((err, _instance, info) => {
  console.error('[MarkdownEditor] Error captured:', err, 'info:', info)
  console.error('[MarkdownEditor] Error message:', err instanceof Error ? err.message : String(err))
  console.error('[MarkdownEditor] Error stack:', err instanceof Error ? err.stack : 'N/A')
  return false // Don't propagate
})

// Only sync modelValue in non-collaborative mode
// Skip if change originated from editor itself (prevents cursor jumping)
watch(() => props.modelValue, (value) => {
  if (props.collaborative) return
  // Skip if this update was triggered by the editor's onUpdate (internal change)
  if (isInternalUpdate.value) return

  const currentContent = editor.value?.getHTML()
  const htmlContent = ensureHtml(value || '')
  if (htmlContent !== currentContent) {
    editor.value?.commands.setContent(htmlContent, { emitUpdate: false })
  }
})

watch(() => props.readonly, (readonly) => {
  if (previewMode.value) return
  editor.value?.setEditable(!readonly)
})

async function handleImageUpload(file: File) {
  const { entityType, entityId } = getEntityInfo()

  // For new entities or articles without attachment support, use base64
  if (!entityId || entityType === 'article') {
    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string
      editor.value?.chain().focus().setImage({ src: dataUrl }).run()
    }
    reader.readAsDataURL(file)
    return
  }

  // For existing tasks, upload to MinIO
  uploading.value = true
  try {
    const response = await attachmentsApi.upload(entityId, file, { inline: true })
    const attachment = response.data
    // Use permanent backend URL instead of presigned MinIO URL (which expires)
    const imageUrl = `/api/v1/attachments/${attachment.id}/download`
    editor.value?.chain().focus().setImage({ src: imageUrl }).run()
    emit('imageUploaded', imageUrl)
  } catch (e) {
    console.error('Failed to upload image:', e)
    // Fallback to base64
    const reader = new FileReader()
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string
      editor.value?.chain().focus().setImage({ src: dataUrl }).run()
    }
    reader.readAsDataURL(file)
  } finally {
    uploading.value = false
  }
}

function setLink() {
  const previousUrl = editor.value?.getAttributes('link').href || ''
  const url = window.prompt('URL ссылки:', previousUrl)

  if (url === null) return
  if (url === '') {
    editor.value?.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }

  // Check if it's a task reference (#task-id or TASK-123)
  if (url.match(/^#?[a-zA-Z0-9-]+$/)) {
    editor.value?.chain().focus().extendMarkRange('link').setLink({ href: `/tasks/${url.replace('#', '')}` }).run()
  } else {
    editor.value?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }
}

function insertTaskReference() {
  const taskId = window.prompt('ID или номер задачи:')
  if (!taskId) return

  const cleanId = taskId.replace('#', '').trim()
  editor.value?.chain().focus()
    .insertContent({
      type: 'text',
      marks: [{ type: 'link', attrs: { href: `/tasks/${cleanId}` } }],
      text: `#${cleanId}`,
    })
    .insertContent(' ')
    .run()
}

const fileInput = ref<HTMLInputElement | null>(null)

function triggerImageUpload() {
  fileInput.value?.click()
}

function handleImageSelect(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    handleImageUpload(file)
  }
  input.value = ''
}

// Connection status computed
const connectionStatus = computed(() => {
  if (!props.collaborative) return null
  if (isOffline.value) return 'offline'
  if (!wsProvider.value) return 'disconnected'
  if (isSynced.value) return 'synced'
  if (isConnected.value) return 'connected'
  return 'connecting'
})

const connectionStatusColor = computed(() => {
  switch (connectionStatus.value) {
    case 'synced': return 'success'
    case 'connected': return 'warning'
    case 'offline': return 'info'
    case 'connecting': return 'grey'
    case 'disconnected': return 'error'
    default: return 'grey'
  }
})

const connectionStatusText = computed(() => {
  switch (connectionStatus.value) {
    case 'synced': return 'Синхронизировано'
    case 'connected': return 'Подключено'
    case 'offline': return 'Offline (изменения сохранены локально)'
    case 'connecting': return 'Подключение...'
    case 'disconnected': return 'Отключено'
    default: return ''
  }
})

onBeforeUnmount(() => {
  disposed = true
  // Finalize document before unmount to ensure history/annotations are triggered
  wsProvider.value?.finalize()
  if (props.collaborative) {
    teardownCollaboration()
  } else {
    editor.value?.destroy()
  }
})
</script>

<template>
  <div class="markdown-editor">
    <v-alert
      v-if="discardedText !== null"
      type="warning"
      variant="tonal"
      density="compact"
      closable
      class="mb-2"
      @click:close="discardedText = null"
    >
      Текст изменили в другом месте, пока редактор был без связи с сервером:
      показана новая версия, ваши несохранённые правки не применены.
      <v-btn
        size="small"
        variant="text"
        @click="copyDiscarded"
      >
        Скопировать мою версию
      </v-btn>
    </v-alert>
    <!-- Toolbar -->
    <v-toolbar
      v-if="!readonly && editor && !previewMode"
      density="compact"
      class="rounded-t editor-toolbar"
    >
      <v-btn-toggle
        multiple
        variant="text"
        density="compact"
        class="toolbar-group"
      >
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('bold') }"
          @click="editor?.chain().focus().toggleBold().run()"
        >
          <v-icon icon="mdi-format-bold" />
        </v-btn>
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('italic') }"
          @click="editor?.chain().focus().toggleItalic().run()"
        >
          <v-icon icon="mdi-format-italic" />
        </v-btn>
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('underline') }"
          @click="editor?.chain().focus().toggleUnderline().run()"
        >
          <v-icon icon="mdi-format-underline" />
        </v-btn>
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('strike') }"
          @click="editor?.chain().focus().toggleStrike().run()"
        >
          <v-icon icon="mdi-format-strikethrough" />
        </v-btn>
      </v-btn-toggle>

      <v-divider
        vertical
        class="mx-0"
      />

      <v-btn-toggle
        variant="text"
        density="compact"
        class="toolbar-group"
      >
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('heading', { level: 1 }) }"
          @click="editor?.chain().focus().toggleHeading({ level: 1 }).run()"
        >
          <v-icon icon="mdi-format-header-1" />
        </v-btn>
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('heading', { level: 2 }) }"
          @click="editor?.chain().focus().toggleHeading({ level: 2 }).run()"
        >
          <v-icon icon="mdi-format-header-2" />
        </v-btn>
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('heading', { level: 3 }) }"
          @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()"
        >
          <v-icon icon="mdi-format-header-3" />
        </v-btn>
      </v-btn-toggle>

      <v-divider
        vertical
        class="mx-0"
      />

      <v-btn-toggle
        variant="text"
        density="compact"
        class="toolbar-group"
      >
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('bulletList') }"
          @click="editor?.chain().focus().toggleBulletList().run()"
        >
          <v-icon icon="mdi-format-list-bulleted" />
        </v-btn>
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('orderedList') }"
          @click="editor?.chain().focus().toggleOrderedList().run()"
        >
          <v-icon icon="mdi-format-list-numbered" />
        </v-btn>
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('codeBlock') }"
          @click="editor?.chain().focus().toggleCodeBlock().run()"
        >
          <v-icon icon="mdi-code-tags" />
        </v-btn>
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('blockquote') }"
          @click="editor?.chain().focus().toggleBlockquote().run()"
        >
          <v-icon icon="mdi-format-quote-close" />
        </v-btn>
      </v-btn-toggle>

      <v-divider
        vertical
        class="mx-0"
      />

      <v-btn-toggle
        variant="text"
        density="compact"
        class="toolbar-group"
      >
        <v-btn
          size="small"
          :class="{ 'v-btn--active': editor?.isActive('link') }"
          @click="setLink"
        >
          <v-icon icon="mdi-link" />
        </v-btn>
        <v-btn
          size="small"
          @click="insertTaskReference"
        >
          <v-icon icon="mdi-pound" />
          <v-tooltip
            activator="parent"
            location="bottom"
          >
            Ссылка на задачу (#ID)
          </v-tooltip>
        </v-btn>
        <v-btn
          size="small"
          :loading="uploading"
          @click="triggerImageUpload"
        >
          <v-icon icon="mdi-image" />
          <v-tooltip
            activator="parent"
            location="bottom"
          >
            Вставить изображение
          </v-tooltip>
        </v-btn>
      </v-btn-toggle>

      <v-divider
        vertical
        class="mx-0"
      />

      <!-- AI Improve Button -->
      <v-menu
        v-if="canUseAi"
        v-model="aiMenuOpen"
        location="bottom"
      >
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            size="small"
            variant="text"
            color="primary"
            :loading="aiProcessing"
            class="ai-btn"
          >
            <v-icon icon="mdi-robot" />
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item @click="improveWithAi('all')">
            <template #prepend>
              <v-icon
                icon="mdi-auto-fix"
                size="small"
              />
            </template>
            <v-list-item-title>Улучшить всё</v-list-item-title>
            <v-list-item-subtitle>Исправить ошибки, структурировать</v-list-item-subtitle>
          </v-list-item>
          <v-list-item @click="improveWithAi('fix_errors')">
            <template #prepend>
              <v-icon
                icon="mdi-spellcheck"
                size="small"
              />
            </template>
            <v-list-item-title>Исправить ошибки</v-list-item-title>
            <v-list-item-subtitle>Грамматика и орфография</v-list-item-subtitle>
          </v-list-item>
          <v-list-item @click="improveWithAi('structure')">
            <template #prepend>
              <v-icon
                icon="mdi-format-list-bulleted"
                size="small"
              />
            </template>
            <v-list-item-title>Структурировать</v-list-item-title>
            <v-list-item-subtitle>Заголовки, списки, абзацы</v-list-item-subtitle>
          </v-list-item>
          <v-list-item @click="improveWithAi('clarify')">
            <template #prepend>
              <v-icon
                icon="mdi-text-box-check"
                size="small"
              />
            </template>
            <v-list-item-title>Уточнить</v-list-item-title>
            <v-list-item-subtitle>Сделать понятнее, убрать двусмысленности</v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-spacer />

      <!-- Collaborators -->
      <div
        v-if="collaborative && collaborators.length > 0"
        class="collaborators d-flex align-center mr-2"
      >
        <v-tooltip
          v-for="user in collaborators"
          :key="user.id"
          location="bottom"
        >
          <template #activator="{ props: tooltipProps }">
            <v-avatar
              v-bind="tooltipProps"
              size="24"
              :style="{ backgroundColor: user.color, marginLeft: '-8px', border: '2px solid white' }"
              class="collaborator-avatar"
            >
              <span class="text-white text-caption">{{ user.name[0] }}</span>
            </v-avatar>
          </template>
          {{ user.name }}
        </v-tooltip>
      </div>

      <!-- Connection status -->
      <div
        v-if="connectionStatus"
        class="d-flex align-center mr-1"
      >
        <v-tooltip location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-icon
              v-bind="tooltipProps"
              :icon="connectionStatus === 'synced' ? 'mdi-cloud-check' : connectionStatus === 'connecting' ? 'mdi-cloud-sync' : connectionStatus === 'offline' ? 'mdi-cloud-lock' : 'mdi-cloud-off-outline'"
              :color="connectionStatusColor"
              size="small"
            />
          </template>
          {{ connectionStatusText }}
        </v-tooltip>
      </div>

      <v-btn
        size="small"
        variant="text"
        :disabled="!editor?.can().undo()"
        @click="editor?.chain().focus().undo().run()"
      >
        <v-icon icon="mdi-undo" />
      </v-btn>
      <v-btn
        size="small"
        variant="text"
        :disabled="!editor?.can().redo()"
        @click="editor?.chain().focus().redo().run()"
      >
        <v-icon icon="mdi-redo" />
      </v-btn>
    </v-toolbar>

    <!-- Editor content -->
    <div
      class="editor-content-wrapper"
      :style="{ minHeight: minHeight || '120px' }"
      :class="{ 'rounded-t': readonly, 'readonly': readonly }"
    >
      <EditorContent
        v-if="editor"
        :editor="editor"
        class="editor-content"
      />
      <div
        v-else-if="editorError"
        class="editor-error pa-4"
        :style="{ minHeight: minHeight || '120px' }"
      >
        <v-alert
          type="error"
          variant="tonal"
          class="text-caption"
        >
          <div class="font-weight-bold mb-1">
            Editor Error:
          </div>
          <pre style="white-space: pre-wrap; font-size: 11px;">{{ editorError }}</pre>
        </v-alert>
      </div>
      <div
        v-else
        class="editor-loading d-flex align-center justify-center"
        :style="{ minHeight: minHeight || '120px' }"
      >
        <v-progress-circular
          indeterminate
          size="24"
        />
      </div>
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      style="display: none"
      @change="handleImageSelect"
    >
  </div>
</template>

<style lang="scss">
.markdown-editor {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  overflow: hidden;

  .editor-toolbar {
    background: rgba(var(--v-theme-on-surface), 0.05) !important;
    min-height: 40px !important;
    height: 40px !important;
    padding: 0 8px !important;

    .v-toolbar__content {
      min-height: 40px !important;
      height: 40px !important;
      padding: 0 !important;
      gap: 4px;
      align-items: center;
    }

    .toolbar-group {
      gap: 2px;

      .v-btn {
        min-width: 32px !important;
        width: 32px !important;
        height: 32px !important;
        padding: 0 !important;
      }
    }

    .v-divider--vertical {
      height: 28px;
      margin: 0 6px !important;
      align-self: center;
    }

    // AI button and right-side buttons
    .ai-btn,
    & > .v-btn {
      min-width: 32px !important;
      width: 32px !important;
      height: 32px !important;
      padding: 0 !important;
      align-self: center;
    }

    .toolbar-group {
      align-items: center;
    }
  }

  .collaborators {
    .collaborator-avatar:first-child {
      margin-left: 0 !important;
    }
  }

  .editor-content-wrapper {
    background: rgb(var(--v-theme-surface));
    padding: 12px;

    &.readonly {
      border-radius: 4px;
      background: transparent;
      padding: 0;
    }
  }

  .editor-content {
    outline: none;

    .ProseMirror {
      outline: none;
      min-height: inherit;
      font-size: 16px; /* Prevent mobile browser auto-zoom on focus */

      p.is-editor-empty:first-child::before {
        color: rgba(var(--v-theme-on-surface), 0.5);
        content: attr(data-placeholder);
        float: left;
        height: 0;
        pointer-events: none;
      }

      h1, h2, h3 {
        margin-top: 0.5em;
        margin-bottom: 0.25em;
        font-weight: 600;
      }

      h1 { font-size: 1.5em; }
      h2 { font-size: 1.25em; }
      h3 { font-size: 1.1em; }

      p {
        margin-bottom: 0.5em;
      }

      ul, ol {
        padding-left: 1.5em;
        margin-bottom: 0.5em;
      }

      blockquote {
        border-left: 3px solid rgba(var(--v-theme-primary), 0.5);
        padding-left: 1em;
        margin-left: 0;
        color: rgba(var(--v-theme-on-surface), 0.7);
      }

      code {
        background: rgba(var(--v-theme-on-surface), 0.05);
        padding: 0.2em 0.4em;
        border-radius: 3px;
        font-family: 'Fira Code', 'Monaco', monospace;
        font-size: 0.9em;
      }

      pre {
        background: rgba(var(--v-theme-on-surface), 0.05);
        padding: 0.75em 1em;
        border-radius: 4px;
        overflow-x: auto;
        font-family: 'Fira Code', 'Monaco', 'Consolas', 'Liberation Mono', monospace;
        font-size: 0.85em;
        line-height: 1.4;
        white-space: pre;

        code {
          background: none;
          padding: 0;
          font-family: inherit;
          font-size: inherit;
          line-height: inherit;
        }
      }

      img {
        max-width: 100%;
        height: auto;
        border-radius: 4px;
        margin: 0.5em 0;
      }

      a {
        color: rgb(var(--v-theme-primary));
        text-decoration: underline;
        cursor: pointer;
      }

      mark {
        background-color: rgba(var(--v-theme-warning), 0.3);
        padding: 0.1em 0.2em;
        border-radius: 2px;
      }

      /* Mention styles */
      .mention {
        background-color: rgba(var(--v-theme-primary), 0.15);
        color: rgb(var(--v-theme-primary));
        padding: 0.1em 0.3em;
        border-radius: 4px;
        font-weight: 500;
        text-decoration: none;
        cursor: pointer;

        &:hover {
          background-color: rgba(var(--v-theme-primary), 0.25);
        }
      }

      /* Collaborative cursor styles */
      .collaboration-cursor__caret {
        position: relative;
        margin-left: -1px;
        margin-right: -1px;
        border-left: 1px solid;
        border-right: 1px solid;
        word-break: normal;
        pointer-events: none;
      }

      .collaboration-cursor__label {
        position: absolute;
        top: -1.4em;
        left: -1px;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        user-select: none;
        color: white;
        padding: 0.1rem 0.3rem;
        border-radius: 3px 3px 3px 0;
        white-space: nowrap;
      }
    }
  }
}
</style>
